/**
 * R&R Party utilities — confetti bursts, emoji rain, sparkle poofs, sound FX,
 * and the party-mode toggle. All are best-effort: if canvas-confetti isn't
 * present or user disables sound, everything degrades silently.
 */
import confetti from 'canvas-confetti';

// ---------------------------------------------------------------------------
// Confetti helpers
// ---------------------------------------------------------------------------
export const bigCelebration = () => {
  const duration = 2200;
  const end = Date.now() + duration;
  const colors = ['#f43f5e', '#f59e0b', '#eab308', '#22c55e', '#06b6d4', '#6366f1', '#a855f7', '#ec4899'];

  (function frame() {
    confetti({ particleCount: 6, angle: 60, spread: 55, startVelocity: 60, origin: { x: 0, y: 0.7 }, colors });
    confetti({ particleCount: 6, angle: 120, spread: 55, startVelocity: 60, origin: { x: 1, y: 0.7 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();

  // Center burst
  confetti({
    particleCount: 140,
    spread: 90,
    origin: { y: 0.55 },
    colors,
    scalar: 1.2,
  });
};

export const softConfettiDrizzle = () => {
  confetti({
    particleCount: 40,
    angle: 90,
    spread: 90,
    startVelocity: 20,
    gravity: 0.6,
    ticks: 200,
    origin: { y: 0 },
    colors: ['#f59e0b', '#ec4899', '#06b6d4', '#a855f7', '#22c55e'],
  });
};

export const sparklePoofAt = (x, y) => {
  confetti({
    particleCount: 18,
    startVelocity: 22,
    spread: 360,
    ticks: 40,
    gravity: 0.4,
    scalar: 0.9,
    origin: { x: x / window.innerWidth, y: y / window.innerHeight },
    colors: ['#fde047', '#fbbf24', '#f472b6', '#a5b4fc', '#5eead4'],
    shapes: ['circle'],
  });
};

export const cardBurstFromButton = (buttonEl) => {
  if (!buttonEl) return;
  const rect = buttonEl.getBoundingClientRect();
  sparklePoofAt(rect.left + rect.width / 2, rect.top + rect.height / 2);
};

// ---------------------------------------------------------------------------
// Emoji rain (used on winner-flag in admin)
// ---------------------------------------------------------------------------
export const emojiRain = (emojis = ['🎉', '🏆', '⭐', '🎊', '🥂'], durationMs = 3000) => {
  const container = document.createElement('div');
  container.className = 'rr-emoji-rain';
  document.body.appendChild(container);
  const spawn = () => {
    for (let i = 0; i < 4; i++) {
      const el = document.createElement('span');
      el.textContent = emojis[Math.floor(Math.random() * emojis.length)];
      el.style.left = `${Math.random() * 100}%`;
      el.style.animationDuration = `${2 + Math.random() * 2}s`;
      el.style.fontSize = `${18 + Math.random() * 18}px`;
      container.appendChild(el);
      setTimeout(() => el.remove(), 4500);
    }
  };
  const iv = setInterval(spawn, 180);
  spawn();
  setTimeout(() => { clearInterval(iv); setTimeout(() => container.remove(), 4500); }, durationMs);
};

// ---------------------------------------------------------------------------
// Sound effects (optional, gated by localStorage)
// ---------------------------------------------------------------------------
const SND_KEY = 'rr_party_sound_enabled';
export const isSoundEnabled = () => localStorage.getItem(SND_KEY) !== 'false'; // default on
export const setSoundEnabled = (on) => localStorage.setItem(SND_KEY, on ? 'true' : 'false');

// Cheap WebAudio ding — no external assets needed.
export const playDing = () => {
  if (!isSoundEnabled()) return;
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;
    [880, 1320].forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, now + idx * 0.1);
      g.gain.exponentialRampToValueAtTime(0.15, now + idx * 0.1 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + idx * 0.1 + 0.45);
      osc.connect(g); g.connect(ctx.destination);
      osc.start(now + idx * 0.1);
      osc.stop(now + idx * 0.1 + 0.5);
    });
    setTimeout(() => ctx.close(), 800);
  } catch (e) { /* silent */ }
};

export const playApplause = () => {
  if (!isSoundEnabled()) return;
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const buf = ctx.createBuffer(1, ctx.sampleRate * 1.4, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      // Pink-noise-ish burst with fade
      const fade = 1 - i / data.length;
      data[i] = (Math.random() * 2 - 1) * 0.35 * fade * fade;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 1400;
    filter.Q.value = 0.9;
    src.connect(filter); filter.connect(ctx.destination);
    src.start();
    setTimeout(() => ctx.close(), 1800);
  } catch (e) { /* silent */ }
};

// ---------------------------------------------------------------------------
// Party mode toggle
// ---------------------------------------------------------------------------
const PARTY_KEY = 'rr_party_mode_nuclear';
export const isPartyModeOn = () => localStorage.getItem(PARTY_KEY) === 'true';
export const setPartyMode = (on) => localStorage.setItem(PARTY_KEY, on ? 'true' : 'false');

// ---------------------------------------------------------------------------
// Nominate button label variations
// ---------------------------------------------------------------------------
export const NOMINATE_LABELS = ['Nominate', 'Send Applause', 'Champion Them', 'Give the Nod', 'Cast a Vote'];
export const rotatingLabel = (index) => NOMINATE_LABELS[index % NOMINATE_LABELS.length];

// ---------------------------------------------------------------------------
// Achievement definitions — evaluated from `myNoms` + `stats`
// ---------------------------------------------------------------------------
export const ACHIEVEMENTS = [
  { id: 'first-ballot', emoji: '🎯', title: 'First Ballot', hint: 'File your first nomination.', check: ({ myCount }) => myCount >= 1 },
  { id: 'triple-play', emoji: '🎬', title: 'Triple Play', hint: 'Three nominations locked in.', check: ({ myCount }) => myCount >= 3 },
  { id: 'rainbow', emoji: '🌈', title: 'Rainbow Nominator', hint: 'Cover five different awards.', check: ({ myAwardCount }) => myAwardCount >= 5 },
  { id: 'team-booster', emoji: '🎁', title: 'Team Booster', hint: 'Champion 10 teammates.', check: ({ myNomineeCount }) => myNomineeCount >= 10 },
  { id: 'agency-wide', emoji: '🎨', title: 'Culture Champ', hint: 'File an Agency-Wide nomination.', check: ({ agencyWideCount }) => agencyWideCount >= 1 },
  { id: 'deadline-warrior', emoji: '⚡', title: 'Deadline Warrior', hint: 'File in the closing week.', check: ({ latestNominationAt, closesAt }) => {
      if (!latestNominationAt || !closesAt) return false;
      return new Date(closesAt) - new Date(latestNominationAt) <= 7 * 86400000;
    } },
];

// ---------------------------------------------------------------------------
// Countdown helper
// ---------------------------------------------------------------------------
export const formatCountdown = (targetIso) => {
  if (!targetIso) return null;
  const now = Date.now();
  const then = new Date(targetIso).getTime();
  const diff = then - now;
  if (diff <= 0) return { text: 'Closed', urgent: true, expired: true };
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0 || days > 0) parts.push(`${hours}h`);
  parts.push(`${mins}m`);
  return { text: parts.join(' ') + ' left', urgent: diff < 86400000, expired: false };
};

// ---------------------------------------------------------------------------
// Rotating taglines
// ---------------------------------------------------------------------------
export const TAGLINES = [
  'Nominate boldly.',
  'Call out a quiet hero.',
  'One name, one story.',
  'The loudest applause is the specific one.',
  'Prizes await. Nominators lead the charge.',
];
